import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HELP_CONTACT,
  profileHelpTopicGroups,
  profileHelpTopics,
} from '../content/helpContent';

function HelpTopicRow({
  id,
  title,
  summary,
  expanded,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  summary: string;
  expanded: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-amber-50/50 transition"
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.06)' }}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900">{title}</span>
          {!expanded && (
            <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">{summary}</span>
          )}
        </span>
        <span
          className={`shrink-0 text-amber-700 text-sm transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden
        >
          ▼
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100 text-sm text-gray-600 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

export default function GetHelpSection() {
  const [expandedTopic, setExpandedTopic] = useState<string | null>(null);

  const toggleTopic = (id: string) => {
    setExpandedTopic((prev) => (prev === id ? null : id));
  };

  const topicsByGroup = profileHelpTopicGroups.map((group) => ({
    group,
    topics: profileHelpTopics.filter((t) => t.group === group),
  }));

  return (
    <div className="pt-4 space-y-6">
      <Link
        to="/orders"
        className="block rounded-lg border border-amber-300 bg-amber-50/90 p-4 hover:bg-amber-50 transition"
      >
        <p className="text-sm font-bold text-amber-900">Problem with a delivered order?</p>
        <p className="text-xs text-gray-600 mt-1">
          Open My Orders → select your order → Report an issue (photo required, within 48 hours).
        </p>
      </Link>

      {topicsByGroup.map(({ group, topics }) =>
        topics.length === 0 ? null : (
          <div key={group}>
            <h3 className="text-xs font-bold uppercase tracking-wide text-amber-800/80 mb-2">{group}</h3>
            <div className="space-y-2">
              {topics.map((topic) => (
                <HelpTopicRow
                  key={topic.id}
                  id={topic.id}
                  title={topic.title}
                  summary={topic.summary}
                  expanded={expandedTopic === topic.id}
                  onToggle={toggleTopic}
                >
                  <ul className="list-disc list-inside space-y-1.5 mt-2">
                    {topic.details.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </HelpTopicRow>
              ))}
            </div>
          </div>
        )
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
        <h3 className="text-sm font-bold text-amber-900">Contact us</h3>
        <div className="space-y-3 text-sm">
          <div className="flex gap-3">
            <span className="text-xl shrink-0" aria-hidden>
              💬
            </span>
            <div>
              <p className="font-semibold text-gray-900">Email</p>
              <a href={`mailto:${HELP_CONTACT.email}`} className="text-amber-700 hover:underline">
                {HELP_CONTACT.email}
              </a>
              <p className="text-xs text-gray-500 mt-0.5">{HELP_CONTACT.hours}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="text-xl shrink-0" aria-hidden>
              📞
            </span>
            <div>
              <p className="font-semibold text-gray-900">Phone</p>
              <a href={`tel:${HELP_CONTACT.phone}`} className="text-amber-700 hover:underline">
                {HELP_CONTACT.phoneDisplay}
              </a>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="text-xl shrink-0" aria-hidden>
              ✉️
            </span>
            <div>
              <p className="font-semibold text-gray-900">Feedback</p>
              <a
                href={`mailto:${HELP_CONTACT.feedbackEmail}?subject=QahwaHub%20feedback`}
                className="text-amber-700 hover:underline"
              >
                {HELP_CONTACT.feedbackEmail}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
